/**
 * The DCC boundary, and the three sinks the Session Flight Recorder observes it through (AVS-4433).
 *
 * **This module must keep ZERO imports.** It is the seam between the app and a partner's page, it
 * is reached from `E3DRuntime`'s constructor, from `fireEvent` and from a React effect, and every
 * one of those call sites is on a path where an import cycle or a heavier graph would be paid for
 * by the host. The discipline — and the shape of every sink below — is copied verbatim from
 * `setListenerGuardReporter` / `setEmitRecorder` (`utils/events.ts:61`, `:105`): a module-level
 * settable function defaulting to a no-op, called from INSIDE the owning code, wrapped in its own
 * try/catch so a throwing sink can never fail the host call.
 *
 * **Sinks, never listeners, and every return value is discarded.** `utils/runtime.ts:125` reads
 * `const [prevented] = this.emit(...)` to let the DCC host SUPPRESS a toast or a dialog, and
 * `utils/events.ts` treats a listener returning `false` as "prevent". A recorder wired as
 * `on('dialog', () => enabled && push(e))` would therefore suppress host dialogs whenever the
 * recorder was gated off — silently changing what a dealer sees. Nothing here can do that: the
 * sinks are typed `void`, they are invoked outside the emit protocol, and what they return is
 * dropped on the floor.
 */

export enum IntegrationType {
  Standalone = 'standalone',
  WebComponent = 'wc',
}

export function integrationType() {
  if (globalThis.E3D) {
    return IntegrationType.WebComponent;
  }
  return IntegrationType.Standalone;
}

export function routingEnabled() {
  return integrationType() === IntegrationType.Standalone;
}

export interface IIntegrationEvent {
  el?: HTMLElement | null;
  name: string;
  detail: Record<string, unknown>;
}

/**
 * One outbound event, as it left us — including whether the host cancelled it.
 *
 * `prevented` is the reason this sink exists. The six outbound events are `cancelable`, and a host
 * `preventDefault()` silently changes what the app does: a cancelled `e3d-stream-start` leaves
 * `streamActive: false` (`containers/Stream/View.tsx:265`) and a cancelled `e3d-stream-end` means
 * the session does not end. Today that decision leaves no trace anywhere.
 */
export interface IIntegrationEventRecord {
  name: string;
  detail: Record<string, unknown>;
  /** The element the event was dispatched on — the tag identifies which of our elements it was. */
  target: HTMLElement;
  /** True when the host called `preventDefault()`. */
  prevented: boolean;
}

export type TIntegrationEventSink = (event: IIntegrationEventRecord) => void;

/**
 * Lifecycle phases of our own custom elements.
 *
 * `deck-mounted` / `deck-unmounted` are the `<avp-3dws-deck>` inside `components/Showroom`, which is
 * the one element lifecycle observable from inside React. DCC remounts `<e3d-stream>` on every
 * `__e3dReinitCallback` (after its OIDC login and after a 401 refresh) and that remount tears the
 * React root down with it, so a `deck-unmounted` immediately followed by a `deck-mounted` with a new
 * element identity IS the remount, seen from the only place this lane owns.
 */
export type TElementLifecyclePhase =
  | 'connected'
  | 'disconnected'
  | 'attribute-changed'
  | 'deck-mounted'
  | 'deck-unmounted';

export interface IElementLifecycleDetail {
  attribute?: string;
  from?: string | null;
  to?: string | null;
}

export type TElementLifecycleSink = (
  phase: TElementLifecyclePhase,
  element: Element,
  detail?: IElementLifecycleDetail
) => void;

/**
 * Applied to every `E3DRuntime` the moment it is constructed.
 *
 * The runtime is the DCC boundary's inbound half, and it cannot be observed from outside: `init`,
 * `startSession` and `getSessionData` are prototype methods, while `endSession`, `share`, `showCar`
 * and `resumeSessionFromParams` are own-property class fields assigned from module imports. This
 * hook hands the whole instance over once, after every field initialiser has run, so a lane can
 * cover both halves without guessing at construction order or racing the bundle's own
 * `globalThis.E3D` assignment (`src/index.ts:18`).
 */
export type TRuntimeInstrumentation = (runtime: object) => void;

function noopIntegrationEventSink(): void {
  // Default sink: no recorder installed, and `fireEvent` pays one no-op call.
}

function noopElementLifecycleSink(): void {
  // Default sink: element lifecycle is not observed.
}

function noopRuntimeInstrumentation(): void {
  // Default: an E3DRuntime is constructed uninstrumented, which is the production behaviour.
}

let integrationEventSink: TIntegrationEventSink = noopIntegrationEventSink;
let elementLifecycleSink: TElementLifecycleSink = noopElementLifecycleSink;
let runtimeInstrumentation: TRuntimeInstrumentation = noopRuntimeInstrumentation;

/**
 * Register a sink for every outbound integration event. Pass `null` to restore the no-op.
 *
 * Called from inside {@link fireEvent} AFTER `dispatchEvent` has returned, so the host's
 * `preventDefault()` verdict is already known and the sink cannot influence it.
 */
export function setIntegrationEventSink(sink: TIntegrationEventSink | null): void {
  integrationEventSink = sink ?? noopIntegrationEventSink;
}

/** Register a sink for our custom elements' lifecycle. Pass `null` to restore the no-op. */
export function setElementLifecycleSink(sink: TElementLifecycleSink | null): void {
  elementLifecycleSink = sink ?? noopElementLifecycleSink;
}

/**
 * Register the instrumentation applied to every future `E3DRuntime`. Pass `null` to restore the
 * no-op.
 *
 * Deliberately NOT applied retroactively from here: this module holds no registry of live runtimes,
 * because a module-level `Set` of them would outlive the page's own reference. The one runtime that
 * can already exist when a lane installs is `globalThis.E3D`, which the lane instruments directly.
 */
export function setRuntimeInstrumentation(instrumentation: TRuntimeInstrumentation | null): void {
  runtimeInstrumentation = instrumentation ?? noopRuntimeInstrumentation;
}

/** Test-only: restore every sink to its no-op, so a suite cannot leak one into the next. */
export function resetIntegrationSinks(): void {
  integrationEventSink = noopIntegrationEventSink;
  elementLifecycleSink = noopElementLifecycleSink;
  runtimeInstrumentation = noopRuntimeInstrumentation;
}

/**
 * Report one lifecycle transition of one of our custom elements.
 *
 * The sink is registered from outside and arbitrary — a throwing one must never break a
 * `connectedCallback` or a React cleanup, so only the call itself sits inside the try. `console` is
 * used rather than `utils/log` for the zero-imports reason at the top of this file.
 */
export function notifyElementLifecycle(
  phase: TElementLifecyclePhase,
  element: Element,
  detail?: IElementLifecycleDetail
): void {
  try {
    elementLifecycleSink(phase, element, detail);
  } catch (error) {
    console.error('[app] element-lifecycle sink threw and was isolated', error);
  }
}

/**
 * Report a freshly constructed `E3DRuntime`. Called from its constructor, after the field
 * initialisers, so the own-property API surface is complete by the time a lane sees it.
 */
export function notifyRuntimeCreated(runtime: object): void {
  try {
    runtimeInstrumentation(runtime);
  } catch (error) {
    console.error('[app] runtime instrumentation threw and was isolated', error);
  }
}

/**
 * Fire a custom event on an element that can be captured by the web component.
 * This event can be cancelled with `event.preventDefault()`.
 * If the event is cancelled, the function will return false.
 */
export function fireEvent({ el, name, detail }: IIntegrationEvent) {
  if (!el) {
    return;
  }
  const customEvent = new CustomEvent(name, {
    bubbles: true,
    composed: true,
    cancelable: true,
    detail: {
      ...detail,
    },
  });
  const canContinue = el.dispatchEvent(customEvent);

  // The sink's return value is discarded, deliberately: `canContinue` below is the host's verdict
  // and nothing observing it may change it.
  try {
    integrationEventSink({ name, detail, target: el, prevented: !canContinue });
  } catch (error) {
    console.error('[app] integration-event sink threw and was isolated', error);
  }

  return canContinue;
}
